/**
 * `/fulfillment` (§11.3, §16.2 "Fulfillment", D09): owner, SKU, address region (masked), carrier/tracking or pickup,
 * dispatch SLA, exception age with owner and next update time, delivery evidence upload reference, completion →
 * `fulfillment_completed`. Only the named owner's team (fulfillment.update) acts; others see the queue.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FulfillmentStatus, FulfillmentType, type FulfillmentAdminDto } from '@windrop/contracts';
import { Button, Field, Input, Modal, Select, useIntl, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { fulfillmentApi } from '../../lib/api/claims';
import { isApiError } from '../../lib/api/client';
import { ageText } from '../claims/ClaimsQueuePage';

type Dialog = { kind: 'start' | 'exception' | 'complete'; row: FulfillmentAdminDto } | null;

export function FulfillmentPage() {
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [overdue, setOverdue] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [stage, setStage] = useState('');
  const [note, setNote] = useState('');
  const [nextUpdateHours, setNextUpdateHours] = useState('24');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [completionRef, setCompletionRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['fulfillment', { status, type, overdue }],
    queryFn: () =>
      fulfillmentApi.list({
        status: status ? (status as FulfillmentStatus) : undefined,
        type: type ? (type as FulfillmentType) : undefined,
        overdue: overdue || undefined,
        limit: 100,
      }),
  });
  const action = useMutation({
    mutationFn: async (d: NonNullable<Dialog>) => {
      if (d.kind === 'start')
        return fulfillmentApi.start(d.row.fulfillmentId, {
          ...(carrier ? { carrier } : {}),
          ...(tracking ? { trackingRef: tracking } : {}),
          ...(note ? { note } : {}),
        });
      if (d.kind === 'exception')
        return fulfillmentApi.exception(d.row.fulfillmentId, {
          stage,
          note,
          nextUpdateAt: new Date(Date.now() + Number(nextUpdateHours) * 3_600_000).toISOString(),
        });
      return fulfillmentApi.complete(d.row.fulfillmentId, {
        evidenceRef,
        ...(completionRef ? { completionRef } : {}),
        ...(note ? { note } : {}),
      });
    },
    onSuccess: (_dto, d) => {
      toast({
        title:
          d.kind === 'start'
            ? 'Fulfillment started'
            : d.kind === 'exception'
              ? 'Exception recorded'
              : 'Fulfillment completed',
        tone: 'success',
      });
      setDialog(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['fulfillment'] });
      void qc.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (e) =>
      setError(isApiError(e) ? (e.problem?.detail ?? e.message) : 'Something went wrong. Retry in a moment.'),
  });
  const columns = useMemo<Column<FulfillmentAdminDto>[]>(
    () => [
      {
        id: 'win',
        header: 'Win / prize',
        sortValue: (r) => r.winIdDisplay,
        filterValue: (r) => `${r.winIdDisplay} ${r.prizeName} ${r.sku ?? ''} ${r.owner}`,
        cell: (r) => (
          <>
            <Link to={`/claims/${r.claimId}`}>
              <strong>{r.winIdDisplay}</strong>
            </Link>
            <br />
            <span className="wd-muted">
              {r.prizeName}
              {r.sku ? ` · ${r.sku}` : ''}
            </span>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        cell: (r) => <StatusBadge kind="FulfillmentStatus" value={r.status} />,
      },
      {
        id: 'owner',
        header: 'Owner',
        sortValue: (r) => r.owner,
        cell: (r) => (
          <>
            {r.owner}
            {r.backupOwner ? (
              <>
                <br />
                <span className="wd-muted">backup {r.backupOwner}</span>
              </>
            ) : null}
          </>
        ),
      },
      { id: 'type', header: 'Type', sortValue: (r) => r.type, cell: (r) => humanizeStatus(r.type) },
      { id: 'region', header: 'Destination (region)', cell: (r) => r.destinationRegion ?? '—' },
      {
        id: 'carrier',
        header: 'Carrier / pickup',
        cell: (r) =>
          r.carrier || r.trackingRef
            ? `${r.carrier ?? ''} ${r.trackingRef ?? ''}`.trim()
            : r.type === 'PHYSICAL'
              ? 'not dispatched'
              : '—',
      },
      {
        id: 'sla',
        header: 'SLA due',
        sortValue: (r) => r.slaDueAt ?? '',
        cell: (r) =>
          r.slaDueAt ? (
            <span data-testid="fulfillment-sla">
              {intl.formatDateTime(r.slaDueAt)}{' '}
              {r.overdue ? <ToneBadge tone="error">Overdue</ToneBadge> : null}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'exception',
        header: 'Exception',
        cell: (r) =>
          r.exception ? (
            <span data-testid="fulfillment-exception">
              <ToneBadge tone="error">{r.exception.stage}</ToneBadge> {ageText(r.exception.ageSeconds)} old ·{' '}
              {r.exception.owner} · next update{' '}
              {r.exception.nextUpdateAt ? intl.formatDateTime(r.exception.nextUpdateAt) : '—'}
              <br />
              <span className="wd-muted">{r.exception.note}</span>
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'evidence',
        header: 'Evidence',
        cell: (r) => (r.evidenceRef ? <code>{r.evidenceRef}</code> : '—'),
      },
    ],
    [intl],
  );
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Fulfillment' }]}
        title="Fulfillment"
        description="Owner, SKU, destination region, carrier or pickup, dispatch SLA and exceptions. Completion needs delivery evidence and moves the claim to paid / delivered."
        actions={
          <div className="wd-btn-group">
            <Select
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[
                { value: '', label: 'Active' },
                ...FulfillmentStatus.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
              ]}
            />
            <Select
              aria-label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={[
                { value: '', label: 'All types' },
                ...FulfillmentType.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
              ]}
            />
            <Button
              variant={overdue ? 'primary' : 'secondary'}
              onClick={() => setOverdue((v) => !v)}
              aria-pressed={overdue}
            >
              Overdue only
            </Button>
          </div>
        }
      />
      {query.isError ? (
        <ErrorState
          title="The fulfillment queue did not load"
          body="Retry in a moment."
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<FulfillmentAdminDto>
          caption="Fulfillments by SLA due date"
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.fulfillmentId}
          loading={query.isPending}
          filterable
          emptyState={
            <EmptyState
              title="Nothing to fulfil"
              body="Approved claims appear here as READY."
              action={<Button href="/claims">Open the claims queue</Button>}
            />
          }
          rowActions={(r) => (
            <Can permission="fulfillment.update">
              <div className="wd-btn-group">
                {r.status === 'READY' || r.status === 'EXCEPTION' ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setDialog({ kind: 'start', row: r });
                      setError(null);
                    }}
                  >
                    {r.status === 'EXCEPTION' ? 'Resume' : 'Start'}
                  </Button>
                ) : null}
                {r.status === 'READY' || r.status === 'IN_PROGRESS' ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDialog({ kind: 'exception', row: r });
                      setError(null);
                    }}
                  >
                    Exception
                  </Button>
                ) : null}
                {r.status === 'IN_PROGRESS' || r.status === 'EXCEPTION' ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDialog({ kind: 'complete', row: r });
                      setError(null);
                    }}
                  >
                    Complete
                  </Button>
                ) : null}
              </div>
            </Can>
          )}
        />
      )}
      <Modal
        open={dialog?.kind === 'start'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Start fulfillment"
        description="Carrier and tracking for dispatch, or pickup instructions."
        actions={
          <Button
            variant="primary"
            loading={action.isPending}
            onClick={() => dialog && action.mutate(dialog)}
          >
            Start
          </Button>
        }
      >
        {error ? <p role="alert">{error}</p> : null}
        <Field label="Carrier">
          <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        </Field>
        <Field label="Tracking reference">
          <Input value={tracking} onChange={(e) => setTracking(e.target.value)} />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </Modal>
      <Modal
        open={dialog?.kind === 'exception'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Record an exception"
        description="The winner sees the stage, the owner and the next update time (never “processing” indefinitely)."
        actions={
          <Button
            variant="primary"
            loading={action.isPending}
            disabled={stage.trim().length < 2 || note.trim().length < 3}
            onClick={() => dialog && action.mutate(dialog)}
          >
            Record
          </Button>
        }
      >
        {error ? <p role="alert">{error}</p> : null}
        <Field label="Stage" required>
          <Input
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            placeholder="dispatch, customs, delivery…"
          />
        </Field>
        <Field label="Participant-safe note" required>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Field label="Next update in (hours)" required>
          <Input
            type="number"
            min={1}
            value={nextUpdateHours}
            onChange={(e) => setNextUpdateHours(e.target.value)}
          />
        </Field>
      </Modal>
      <Modal
        open={dialog?.kind === 'complete'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Complete with delivery evidence"
        description="Signed pickup, carrier proof of delivery, transfer receipt or voucher reference. Emits fulfillment_completed."
        actions={
          <Button
            variant="primary"
            loading={action.isPending}
            disabled={!evidenceRef.trim()}
            onClick={() => dialog && action.mutate(dialog)}
          >
            Complete
          </Button>
        }
      >
        {error ? <p role="alert">{error}</p> : null}
        <Field label="Evidence reference (storage / export key)" required>
          <Input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} />
        </Field>
        <Field label="Completion reference (transfer / voucher / handover id)">
          <Input value={completionRef} onChange={(e) => setCompletionRef(e.target.value)} />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
